import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import type { Stripe } from "@stripe/stripe-js";
import { UUID } from "io-ts-types";

import { CurrencyValue } from "@every.org/common/src/codecs/currency";
import {
  ABTestingId,
  PersonalUserResponse,
  GuestUserResponse,
  Uuid,
} from "@every.org/common/src/codecs/entities";
import { SafeInt } from "@every.org/common/src/codecs/number";
import {
  CreateGuestUserResponseStatus,
  DonationFrequency,
  PaymentSourceType,
} from "@every.org/common/src/entity/types";
import {
  archiveDonationRouteSpec,
  createCardPaymentIntentRouteSpec,
} from "@every.org/common/src/routes/donate";
import { createGuestUserRouteSpec } from "@every.org/common/src/routes/me";

import { updateAuthState } from "src/context/AuthContext/actions";
import { isLoggedInUser } from "src/context/AuthContext/selectors";
import { StripeClientError } from "src/errors/StripeClientError";
import { EdoRouter } from "src/hooks/useEdoRouter";
import { queryApi } from "src/utility/apiClient";
import { getUTMValuesFromCookie } from "src/utility/cookies";
import { logger } from "src/utility/logger";

export type CreateGuestUserOutcome =
  | { proceed: true }
  | { proceed: false; userFacingMessage: string };

export async function createGuestUserIfNeeded({
  router,
  loggedInOrGuestUser,
  firstName,
  lastName,
  email,
  abTestingId,
  cfTurnstileToken,
}: {
  router: EdoRouter;
  loggedInOrGuestUser?: PersonalUserResponse | GuestUserResponse;
  firstName?: string;
  lastName?: string;
  email?: string;
  abTestingId?: ABTestingId | undefined;
  cfTurnstileToken?: string | undefined;
}): Promise<CreateGuestUserOutcome> {
  if (
    loggedInOrGuestUser &&
    // TODO(#12493) this is an issue b/c if the user changes any of their details, any
    // existing attached payment methods will now be attached to a different
    // user and cause an error when trying to donate
    loggedInOrGuestUser.firstName === firstName &&
    loggedInOrGuestUser.lastName === lastName &&
    loggedInOrGuestUser.email === email
  ) {
    return { proceed: true };
  }

  try {
    const guestUserResult = await queryApi(createGuestUserRouteSpec, {
      body: {
        abTestingId,
        firstName,
        lastName,
        email,
        metadata: getUTMValuesFromCookie(),
        cfTurnstileToken,
      },
      routeTokens: {},
      queryParams: {},
    });

    if (
      guestUserResult.status ===
        CreateGuestUserResponseStatus.LOGGED_IN_USER_FOUND &&
      !isLoggedInUser(loggedInOrGuestUser)
    ) {
      logger.warn({
        message: "Guest donation attempted from a logged-in session",
        data: { loggedInUserId: guestUserResult.user.id },
      });
      updateAuthState(guestUserResult.user);
      const accountLabel = guestUserResult.user.email
        ? `as ${guestUserResult.user.email}`
        : "to an existing account";
      return {
        proceed: false,
        userFacingMessage: `You have signed in ${accountLabel}. Please check your details and try again.`,
      };
    }

    updateAuthState(guestUserResult.user);
    return { proceed: true };
  } catch (e) {
    // Not treated as blocking: if the guest really is missing, the donate
    // request that follows reports its own error.
    logger.error({ message: "Error creating guest user", error: e });
    return { proceed: true };
  }
}

/**
 * Pre-authorizes an already-known payment method (a saved card source, or
 * an Apple/Google Pay wallet PaymentMethod - never a bank/ACH source, see
 * submitDonation's isBankPayment check) against a brand-new,
 * manual-capture PaymentIntent, confirming it client-side - same "confirm
 * up front, capture server-side once /donate creates the DonationCharge"
 * shape as the guest new-card flow (see AddCreditCard/createCardPaymentIntentForNewDonation).
 * Required so payWithStripe's on-session branch can assume every Stripe
 * on-session card charge already has a confirmed, requires_capture
 * PaymentIntent - createAndConfirmPaymentIntent is used instead for
 * off-session recurring charges and all bank/ACH payments, neither of
 * which can (or need to) resolve a 3DS/SCA-style challenge client-side.
 */
export async function confirmExistingPaymentMethod({
  stripe,
  toNonprofitId,
  value,
  frequency,
  externalPaymentSourceId,
  paymentSourceType,
  tipAmount,
  creditAmount,
  cfTurnstileToken,
}: {
  stripe: Stripe;
  toNonprofitId: Uuid;
  value: CurrencyValue;
  frequency: DonationFrequency;
  externalPaymentSourceId: string;
  paymentSourceType?: PaymentSourceType;
  tipAmount?: SafeInt;
  creditAmount?: SafeInt;
  cfTurnstileToken?: string;
}): Promise<{ paymentMethodId: string; paymentIntentId: string }> {
  const { clientSecret, paymentIntentId } = await queryApi(
    createCardPaymentIntentRouteSpec,
    {
      body: {
        toNonprofitId,
        value,
        frequency,
        cfTurnstileToken,
        externalPaymentSourceId,
        paymentSourceType,
        tipAmount,
        creditAmount,
      },
      routeTokens: {},
      queryParams: {},
    },
    { disableUnauthenticatedToast: true }
  );
  // The PaymentIntent already has this PaymentMethod attached
  // (createCardPaymentIntent sets payment_method server-side), so no need
  // to pass it again here.
  const { error, paymentIntent } = await stripe.confirmCardPayment(
    clientSecret
  );
  if (error) {
    throw new StripeClientError(
      error,
      "Unable to verify this payment method. Please try again."
    );
  }
  if (!paymentIntent) {
    throw new Error("Unable to verify this payment method. Please try again.");
  }
  return { paymentMethodId: externalPaymentSourceId, paymentIntentId };
}

export async function archiveDonation(
  donationId: UUID | undefined,
  formContext: DonateFormContext
) {
  await formContext.refreshNonce();
  formContext.setCreateOrUpdateDonationResult(undefined);
  if (donationId) {
    await queryApi(archiveDonationRouteSpec, {
      body: { donationId, cancelAndExpireNewCharge: true },
      routeTokens: {},
      queryParams: {},
    });
  }
}
